import { PhoneFormShape } from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/PhoneForm';
import {
    getE164FormattedNumber,
    getNationalPhoneNumberForm,
} from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/utils/phoneForm';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useWindowIsMobile } from '@liveauctioneers/hammer-ui-core/providers';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import ms from 'ms';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { modalTypes } from '@/enums';
import { AlertTypes, DeliveryType } from '@/redux/api/alerts';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isCompleteAccountModalOpen } from '@/redux/modules/account/shouldLogin/shouldLogin.selectors';
import { updateAccountDetails } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import {
    getBidderEmail,
    getBidderPhoneNumber,
    getDefaultAddressPostalCode,
    getUserFirstName,
    getUserLastName,
} from '@/redux/modules/account/user/user.selectors';
import { getCookie, setCookie } from '@/redux/modules/cookies';
import { getSavedItemsCount } from '@/redux/modules/item/saved/savedItems.selectors';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import { getSubscribedToSMSAlerts, toggleSmsAlert } from '@/redux/modules/smsAlerts';
import {
    TrackEnableSmsOnSavePromptActionTaken,
    useTrackEnableSmsOnSavePrompt,
    useTrackSmsNotificationToggle,
} from '@/utils/amplitude/analytics/notifications';
import { TrackSmsNotificationToggleEventTrigger } from '@/utils/amplitude/analytics/notifications/trackSmsNotificationToggle.analytics';
import { isNotificationsPage } from '@/utils/urls';

import { EnableSmsOnSaveModal } from './components/EnableSmsOnSaveModal/EnableSmsOnSaveModal';
import { EnableSmsOnSavePopover } from './components/EnableSmsOnSavePopover/EnableSmsOnSavePopover';

const COOKIE_MAX_AGE = ms('60 days') / 1000; // 60 days in seconds
const COOKIE_NAME = 'dismissableEnableSmsOnSavePrompt';
const COOKIE_BODY = 'dismissed';

export type EnableSmsOnSaveForm = {
    isFormSubmitAttempted: boolean;
    isSubmitLoading: boolean;
    isValid: boolean;
    phoneData: PhoneFormShape;
};

// The main wrapper component that conditionally renders a modal or a popover
export const EnableSmsOnSavePrompt = () => {
    const { formatMessage } = useIntl();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const isMobile = useWindowIsMobile();
    const isOnNotificationsPage = isNotificationsPage(location.pathname);

    const firstName = useAppSelector(getUserFirstName);
    const lastName = useAppSelector(getUserLastName);
    const email = useAppSelector(getBidderEmail);
    const postalCode = useAppSelector(getDefaultAddressPostalCode);

    const openModal = useAppSelector(getOpenModal);

    const hasDismissedCookie = useAppSelector((state) => getCookie(state, COOKIE_NAME)) === COOKIE_BODY;
    const completeAccountModalOpen = useAppSelector(isCompleteAccountModalOpen);
    const hasSmsAlertsEnabled = useAppSelector(getSubscribedToSMSAlerts);

    const bidderPhoneNumber = useAppSelector(getBidderPhoneNumber);
    const savedItemCount = useAppSelector((state) => getSavedItemsCount(state));
    const prevSavedItemCount = usePrevious(savedItemCount);

    const [hasScrolled, setHasScrolled] = useState(false);

    const [showWidget, setShowWidget] = useState(false);
    const [renderTransition, setRenderTransition] = useState(false);
    const initialPhoneForm = getNationalPhoneNumberForm(bidderPhoneNumber || '');

    // AMPLITUDE TRACKING
    const trackEnableSmsOnSavePrompt = useTrackEnableSmsOnSavePrompt();
    const trackSmsNotificationToggle = useTrackSmsNotificationToggle();

    const methods = useForm<EnableSmsOnSaveForm>({
        defaultValues: {
            isFormSubmitAttempted: false,
            isSubmitLoading: false,
            isValid: Boolean(getE164FormattedNumber(initialPhoneForm)),
            phoneData: initialPhoneForm,
        },
    });

    const {
        formState: { dirtyFields, errors },
        handleSubmit,
        reset,
        setValue,
    } = methods;

    // For the first time the widget is shown, we need to set the phone number
    // in the form if there is a delay in fetching the phone number from Redux state
    useEffect(() => {
        if (showWidget && bidderPhoneNumber && !dirtyFields.phoneData) {
            const form = getNationalPhoneNumberForm(bidderPhoneNumber);
            setValue('phoneData', form, { shouldDirty: false, shouldValidate: false });
            setValue('isValid', Boolean(getE164FormattedNumber(form)), { shouldDirty: false, shouldValidate: false });
        }
    }, [bidderPhoneNumber, dirtyFields.phoneData, setValue, showWidget]);

    // Reset the form when the widget opens (or closes),
    // so each open re-derives phoneData from bidderPhoneNumber
    useEffect(() => {
        if (showWidget) {
            reset(
                {
                    isFormSubmitAttempted: false,
                    isSubmitLoading: false,
                    isValid: Boolean(getE164FormattedNumber(getNationalPhoneNumberForm(bidderPhoneNumber || ''))),
                    phoneData: getNationalPhoneNumberForm(bidderPhoneNumber || ''),
                },
                { keepDirty: false, keepErrors: false, keepTouched: false }
            );
        }
    }, [showWidget, bidderPhoneNumber, reset]);

    const useModal = isMobile || hasScrolled;

    const handleSetCookie = useCallback((): void => {
        dispatch(
            setCookie({
                maxAge: COOKIE_MAX_AGE,
                name: COOKIE_NAME,
                value: COOKIE_BODY,
            })
        );
    }, [dispatch]);

    const onCloseSaveWidget = useCallback(
        (isModal?: boolean) => {
            setRenderTransition(false);
            if (isModal) {
                setShowWidget(false);
            }
            // Wait for close transition to complete before setting cookie
            setTimeout(() => {
                handleSetCookie();
            }, 300);
        },
        [handleSetCookie]
    );

    const onTextMeClick = useCallback(
        async (data: EnableSmsOnSaveForm) => {
            setValue('isFormSubmitAttempted', true);

            const formattedPhoneNumber = getE164FormattedNumber(data.phoneData);
            const hasPhoneInputError = !formattedPhoneNumber || Boolean(errors.phoneData?.message) || !data.isValid;

            if (hasPhoneInputError) {
                return;
            }

            setValue('isSubmitLoading', true);
            await dispatch(
                updateAccountDetails({
                    email,
                    firstName,
                    lastName,
                    mobile: formattedPhoneNumber,
                    zipcode: postalCode,
                })
            );
            await dispatch(
                toggleSmsAlert(AlertTypes.All, DeliveryType.sms, true, true, () => {
                    //   Handle error state
                    trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Error });
                    setValue('isSubmitLoading', false);
                    return;
                })
            );

            trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Accepted });
            trackSmsNotificationToggle({
                auctionRemindersEnabled: true,
                eventTrigger: TrackSmsNotificationToggleEventTrigger.SavePrompt,
                outbidEnabled: true,
                paymentRemindersEnabled: true,
            });

            setValue('isSubmitLoading', false);
            onCloseSaveWidget();
            toast('success', {
                description: formatMessage({ id: 'enableSmsOnSave.toast.description' }),
                title: formatMessage({ id: 'enableSmsOnSave.toast.title' }),
            });
        },
        [
            setValue,
            errors.phoneData?.message,
            dispatch,
            email,
            firstName,
            lastName,
            postalCode,
            trackEnableSmsOnSavePrompt,
            trackSmsNotificationToggle,
            onCloseSaveWidget,
            formatMessage,
        ]
    );

    const onOutsideTooltipClick = useCallback(() => {
        trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Closed });
        setRenderTransition(false);
    }, [trackEnableSmsOnSavePrompt]);

    useEffect(() => {
        window.requestAnimationFrame(() => {
            setRenderTransition(showWidget);
        });
    }, [showWidget]);

    useEffect(() => {
        const shouldTriggerWidget =
            prevSavedItemCount !== undefined &&
            savedItemCount > prevSavedItemCount &&
            !hasDismissedCookie &&
            openModal === modalTypes.NONE &&
            !hasSmsAlertsEnabled;

        if (shouldTriggerWidget) {
            setHasScrolled(Boolean(window.scrollY > 0));
            setShowWidget(true);
        }
    }, [
        prevSavedItemCount,
        savedItemCount,
        hasDismissedCookie,
        openModal,
        hasSmsAlertsEnabled,
        trackEnableSmsOnSavePrompt,
    ]);

    const canShowWidget =
        !hasDismissedCookie && !completeAccountModalOpen && !hasSmsAlertsEnabled && !isOnNotificationsPage;

    useEffect(() => {
        if (canShowWidget && showWidget) {
            trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Viewed });
        }
    }, [canShowWidget, showWidget, trackEnableSmsOnSavePrompt]);

    return (
        <FormProvider {...methods}>
            {canShowWidget && (
                <form
                    data-testid="enableSmsOnSaveForm"
                    id="enableSmsOnSaveForm"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSubmit(onTextMeClick)();
                        }
                    }}
                    onSubmit={handleSubmit(onTextMeClick)}
                >
                    {useModal && (
                        <EnableSmsOnSaveModal
                            onCloseSaveWidget={onCloseSaveWidget}
                            setShow={setShowWidget}
                            show={showWidget}
                        />
                    )}
                    {!useModal && showWidget && (
                        <EnableSmsOnSavePopover
                            onCloseSaveWidget={onCloseSaveWidget}
                            onOutsideTooltipClick={onOutsideTooltipClick}
                            renderTransition={renderTransition}
                            setShowWidget={setShowWidget}
                            showWidget={showWidget}
                        />
                    )}
                </form>
            )}
        </FormProvider>
    );
};
